import { TableHead, Th, Tr, Td } from '../../Elements/DataTable';
import React, { useEffect, useMemo } from 'react';
import { FaEdit, FaTrash, FaBoxes, FaEyeSlash, FaEye } from 'react-icons/fa';
import notFoundImage from "../../../assets/fox-not-found.webp";
import { getImageCacheKey, getImageUrl } from "../../../lib/helpers";
import { retireBlockedReason, filtrarPorPildora, mensajeSinProductos, esRetirado, stockBloqueadoMotivo } from "../../../lib/helpers/products";
import PildorasCategoria from "./PildorasCategoria";

// Tablet apaisada real del club (1280×800, sidebar de 256px — ver CLAUDE.md):
// las 8 columnas a `px-4` de fábrica (Th/Td, DataTable.jsx) miden ~992px en
// un hueco real de ~926px — 66px de scroll horizontal, con "Acciones"
// cortada. Th/Td CEDEN su padding al className del caller (por diseño, ver
// DataTable.jsx y su candado de CI): se recorta aquí, por celda, en vez de
// con CSS apuntando a `th`/`td` — un CSS así compite con el componente
// compartido y el candado lo bloquea. Solo pantallas táctiles en apaisado en
// ese rango de ancho; escritorio con ratón y el resto de anchos no se tocan.
const CELL_COMPACT =
  '[@media(pointer:coarse)_and_(orientation:landscape)_and_(min-width:1280px)_and_(max-width:1400px)]:px-2';

// Icono por tipo de ubicación (almacén principal / nevera / armario / sub-almacén)
const LOC_ICON = { principal: '🏠', nevera: '🧊', armario: '🗄️', sub_almacen: '📦' };
const locIcon = (kind) => LOC_ICON[kind] || '📍';
const fmtQty = (n) => Number(n || 0).toLocaleString('es-ES', { maximumFractionDigits: 3 });

const LocBreakdown = ({ list = [], unitType }) => {
  if (!list.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {list.map((l) => {
        const neg = Number(l.qty) < 0;
        return (
        <span key={l.id} className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium ${neg ? 'bg-red-50 text-[#DC2626] ring-1 ring-red-200' : 'bg-gray-100 text-gray-600'}`} title={neg ? `${l.name} · en negativo, pendiente de reponer` : l.name}>
          <span aria-hidden>{neg ? '⚠️' : locIcon(l.kind)}</span>
          <span className="max-w-[90px] truncate">{l.name}</span>
          <span className={`font-semibold ${neg ? 'text-[#DC2626]' : 'text-gray-800'}`}>{fmtQty(l.qty)}{unitType === 'fixed' ? 'u' : 'g'}</span>
        </span>
        );
      })}
    </div>
  );
};

const ProductTable = ({ products, loading, onEdit, onDelete, onStock, onToggleActive, onToggleSelect, selectedIds = [], token, venueID, refreshTrigger, readOnly = false, canEdit = true, searchQuery = "", onVisibleProductsChange, selectedCategory = "All", onCategoryChange, categories: externalCategories, stockByLoc = {} }) => {
  // Categories are owned by Products.jsx which fetches them with the correct
  // x-venue-id. No independent fetch here to avoid races.
  // Solo un array vale: con cualquier otra cosa (un `{}` de un mock o de un
  // backend a medio desplegar) `.filter` reventaba la pantalla entera.
  const allCategories = useMemo(() => (Array.isArray(externalCategories) ? externalCategories : []), [externalCategories]);

  // Compartido con ProductGrid: la selección masiva opera sobre lo visible, así
  // que las dos vistas DEBEN filtrar idéntico (ver helper).
  const filteredProducts = useMemo(
    () => filtrarPorPildora(products, selectedCategory),
    [products, selectedCategory]
  );

  useEffect(() => {
    if (typeof onVisibleProductsChange !== 'function') return;
    const ids = (filteredProducts || []).map((p) => p.id);
    onVisibleProductsChange(ids);
  }, [filteredProducts, onVisibleProductsChange]);

  if (loading) {
    return (
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex-auto">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-500">Cargando productos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex-auto">
      {/* Píldoras de categoría: el mismo render que la vista de tarjetas. */}
      <div className="mb-6 rounded-md border border-[var(--border)] bg-[var(--card)] p-4">
        <PildorasCategoria categories={allCategories} selected={selectedCategory} onChange={onCategoryChange} />
      </div>

      {/* Estado vacío: mantener filtros visibles */}
      {(!filteredProducts || filteredProducts.length === 0) && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center text-gray-500 mb-6">
          {mensajeSinProductos(searchQuery, selectedCategory)}
        </div>
      )}

      {/* Listado responsive (móvil + tablet) */}
      <div className="block xl:hidden space-y-3">
        {(filteredProducts || []).map((product) => {
          const imageUrl = getImageUrl(product.image_path, notFoundImage);

          return (
            <div
              key={product.id}
              className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-blue-300 transition-all duration-200${esRetirado(product) ? " opacity-50 hover:opacity-90" : ""}`}
            >
              <div className="mb-3 flex justify-end">
                {!readOnly ? (
                  <input
                    type="checkbox"
                    checked={selectedIds.some((id) => String(id) === String(product.id))}
                    onChange={() => onToggleSelect(product.id)}
                    className="h-4 w-4 accent-blue-600"
                  />
                ) : null}
              </div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={imageUrl}
                  alt={product.name}
                  className="w-[5.6rem] h-[5.6rem] rounded-xl object-cover border-2 border-gray-300 flex-shrink-0"
                  onError={(e) => {
                    e.target.src = notFoundImage;
                  }}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 truncate">{product.name || '-'}{product.is_active === false && (<span data-testid="badge-retirado" className="ml-2 align-middle rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#737373]" title="Retirado: no aparece en dispensario, productos ni en la carta del socio">Retirado</span>)}</div>
                  <div className="text-xs text-gray-500 truncate">ID: {product.id?.toString().slice(0, 8) || '-'}</div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4 grid grid-cols-2 gap-3 text-sm">
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 mb-0.5">Categoría</span>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 w-fit">
                    {(() => {
                      const n = product.category?.name;
                      if (!n) return 'Sin categoría';
                      return ['Uncategorized', 'uncategorized'].includes(n) ? 'Sin categoría' : n;
                    })()}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 mb-0.5">Precio</span>
                  <span className="font-semibold text-gray-900">€{product.price || '0.00'}</span>
                </div>
                {!readOnly ? (
                  <div className="flex flex-col">
                    <span className="text-xs text-gray-500 mb-0.5">Stock</span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full w-fit ${
                      (product.stock_quantity || 0) > 10
                        ? 'bg-green-100 text-green-800'
                        : (product.stock_quantity || 0) > 0
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {product.stock_quantity || 0}
                    </span>
                    <LocBreakdown list={stockByLoc[product.id]} unitType={product.unit_type} />
                  </div>
                ) : null}
                <div className="flex flex-col">
                  <span className="text-xs text-gray-500 mb-0.5">Tipo</span>
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 w-fit">
                    {product.unit_type === 'fixed' ? 'Unidad' : 'Peso'}
                  </span>
                </div>
                <div className="flex flex-col col-span-2">
                  <span className="text-xs text-gray-500 mb-0.5">Rentabilidad</span>
                  {product.cost_ex_vat == null ? (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 w-fit">
                      Sin coste informado
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 w-fit">
                      Coste {Number(product.cost_ex_vat).toFixed(2)} EUR
                    </span>
                  )}
                </div>
              </div>

              {(!readOnly && canEdit) ? (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => onEdit(product.id)}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 bg-lime-600 hover:bg-lime-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
                    title="Editar producto"
                  >
                    <FaEdit />
                    <span>Editar</span>
                  </button>
                  <button
                    onClick={() => onDelete(product.id)}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 bg-red-700 hover:bg-red-800 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95"
                    title="Eliminar producto"
                  >
                    <FaTrash />
                    <span>Eliminar</span>
                  </button>
                </div>
              ) : null}
              {/* Retirar/Reactivar: alternativa NO destructiva a Eliminar — la referencia
                  sale de las listas de venta pero conserva ficha, stock e historial. */}
              {(!readOnly && canEdit && onToggleActive) ? (
                <button
                  onClick={() => onToggleActive(product)}
                  className={`mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold shadow-sm transition-all active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 ${
                    product.is_active === false
                      ? 'bg-[#0A0A0A] hover:bg-black text-white'
                      : 'bg-white border border-[#E5E5E5] text-[#0A0A0A] hover:bg-[#F5F5F5]'
                  }`}
                  title={product.is_active === false
                    ? 'Volver a ponerlo a la venta'
                    : (retireBlockedReason(product) || 'Retirar de dispensario y productos sin borrarlo')}
                >
                  {product.is_active === false ? <FaEye /> : <FaEyeSlash />}
                  <span>{product.is_active === false ? 'Reactivar' : 'Retirar'}</span>
                </button>
              ) : null}
              {(!readOnly && canEdit && onStock) ? (
                <button
                  onClick={() => onStock(product.id)}
                  disabled={esRetirado(product)}
                  className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold shadow-sm hover:shadow transition-all active:scale-95 disabled:cursor-not-allowed disabled:grayscale disabled:hover:bg-emerald-600 disabled:hover:shadow-sm disabled:active:scale-100"
                  title={stockBloqueadoMotivo(product) || "Entrar / ajustar stock e historial"}
                >
                  <FaBoxes />
                  <span>Actualizar stock</span>
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="hidden xl:block overflow-x-auto">
        <table className="wa-almacen-table w-full text-left text-gray-700">
          <TableHead>
              <Th className={`w-12 ${CELL_COMPACT}`}><span className="sr-only">Seleccionar</span></Th>
              <Th className={CELL_COMPACT}>Producto</Th>
              <Th className={CELL_COMPACT}>Categoría</Th>
              <Th className={CELL_COMPACT}>Precio</Th>
              <Th className={CELL_COMPACT}>Stock</Th>
              <Th className={CELL_COMPACT}>Tipo</Th>
              <Th className={CELL_COMPACT}>Rentabilidad</Th>
              <Th align="right" className={CELL_COMPACT}>Acciones</Th>
            </TableHead>
          <tbody>
            {(filteredProducts || []).map((product) => {
              const imageUrl = getImageUrl(product.image_path, notFoundImage, {
                cacheKey: getImageCacheKey('product', product.id),
              });

              return (
                <Tr key={product.id} className={esRetirado(product) ? "opacity-50 hover:opacity-90" : ""}>
                  <Td className={CELL_COMPACT}>
                    <input
                      type="checkbox"
                      checked={selectedIds.some((id) => String(id) === String(product.id))}
                      onChange={() => onToggleSelect(product.id)}
                      className="h-4 w-4 accent-blue-600"
                    />
                  </Td>
                  <Td className={CELL_COMPACT}>
                    <div className="flex items-center gap-3">
                      <img
                        src={imageUrl}
                        alt={product.name}
                        className="h-[2.1rem] w-[2.1rem] rounded object-cover border border-gray-300"
                        onError={(e) => {
                          e.target.src = notFoundImage;
                        }}
                      />
                      <div>
                        <div className="font-medium text-gray-900">{product.name || '-'}{product.is_active === false && (<span data-testid="badge-retirado" className="ml-2 align-middle rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#737373]" title="Retirado: no aparece en dispensario, productos ni en la carta del socio">Retirado</span>)}</div>
                        <div className="text-xs text-gray-500">ID: {product.id?.toString().slice(0, 8) || '-'}</div>
                      </div>
                    </div>
                  </Td>
                  <Td className={CELL_COMPACT}>
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {(() => {
                        const n = product.category?.name;
                        if (!n) return 'Sin categoría';
                        return ['Uncategorized', 'uncategorized'].includes(n) ? 'Sin categoría' : n;
                      })()}
                    </span>
                  </Td>
                  <Td className={CELL_COMPACT}>
                    <span className="font-semibold text-gray-900">
                      €{product.price || '0.00'}
                    </span>
                  </Td>
                  <Td className={CELL_COMPACT}>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      (product.stock_quantity || 0) > 10 
                        ? 'bg-green-100 text-green-800' 
                        : (product.stock_quantity || 0) > 0 
                        ? 'bg-yellow-100 text-yellow-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {product.stock_quantity || 0}
                    </span>
                    <LocBreakdown list={stockByLoc[product.id]} unitType={product.unit_type} />
                  </Td>
                  <Td className={CELL_COMPACT}>
                    <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                      {product.unit_type === 'fixed' ? 'Unidad' : 'Peso'}
                    </span>
                  </Td>
                  <Td className={CELL_COMPACT}>
                    {product.cost_ex_vat == null ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                        Sin coste
                      </span>
                    ) : (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">€{Number(product.cost_ex_vat).toFixed(2)}</span>
                        <span className="text-xs text-gray-500">{product.supplier_name || 'Sin proveedor'}</span>
                      </div>
                    )}
                  </Td>
                  <Td align="right" className={`space-x-1 ${CELL_COMPACT}`}>
                    {(!readOnly && canEdit) && (
                      <>
                        {onStock && (
                          <button
                            onClick={() => onStock(product.id)}
                            disabled={esRetirado(product)}
                            className="p-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:cursor-not-allowed disabled:grayscale disabled:hover:bg-emerald-600"
                            title={stockBloqueadoMotivo(product) || "Entrar / ajustar stock e historial"}
                          >
                            <FaBoxes />
                          </button>
                        )}
                        <button
                          onClick={() => onEdit(product.id)}
                          className="p-2 bg-lime-600 hover:bg-lime-800 text-white rounded"
                          title="Editar producto"
                        >
                          <FaEdit />
                        </button>
                        {onToggleActive && (
                          <button
                            onClick={() => onToggleActive(product)}
                            className={`p-2 rounded disabled:cursor-not-allowed disabled:opacity-40 ${
                              product.is_active === false
                                ? 'bg-[#0A0A0A] hover:bg-black text-white'
                                : 'bg-white border border-[#E5E5E5] text-[#0A0A0A] hover:bg-[#F5F5F5]'
                            }`}
                            title={product.is_active === false
                              ? 'Volver a ponerlo a la venta'
                              : (retireBlockedReason(product) || 'Retirar de dispensario y productos sin borrarlo')}
                          >
                            {product.is_active === false ? <FaEye /> : <FaEyeSlash />}
                          </button>
                        )}
                        <button
                          onClick={() => onDelete(product.id)}
                          className="p-2 bg-red-700 hover:bg-red-800 text-white rounded"
                          title="Eliminar producto"
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductTable;
